import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import {
  getNoSaleData,
  getNoSalesFilterList,
  getNoSalesRecordsCount,
  getProductListTags
} from "src/api/bi";
import { FilterParamObj } from "src/pages/Dashboard/interface";
import { resetStore } from "src/store/actions";
import {
  decimal,
  formatToCurrency,
  formatToPercentage,
  getComponentKey,
  getItemTypes
} from "src/utils/genericFunctions";
import stringToNumber from "src/utils/stringToNumber";
import {
  NoSaleInitialState,
  NoSaleRequestParams
} from "../../../config/tableInterface/interface";

export const fetchNoSaleData = createAsyncThunk(
  "viewItems/fetchNoSaleData",
  (payload: NoSaleRequestParams) => getNoSaleData(payload)
);

export const fetchNoSalesRecordsCount = createAsyncThunk(
  "viewItems/fetchNoSalesRecordsCount",
  (payload: NoSaleRequestParams) => getNoSalesRecordsCount(payload)
);

export const fetchNoSalesFilterList = createAsyncThunk(
  "dashboard/filterList",
  (filterParamObj: FilterParamObj) => getNoSalesFilterList(filterParamObj)
);

export const fetchNoSalesListTags = createAsyncThunk(
  "dashboard/fetchProductListTags",
  () => getProductListTags()
);

const initialState: NoSaleInitialState = {
  data: [],
  recordsCount: null,
  status: "idle",
  error: null,
  tags: [],
  filters: null,
  noSalesRequestId: undefined,
  countRequestId: undefined,
  vendorMap: {}
};

const viewNoSaleData = createSlice({
  name: "viewNoSaleData",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchNoSaleData.pending, (state, action) => {
        state.noSalesRequestId = action.meta.requestId;
        state.status = "pending";
      })
      .addCase(fetchNoSaleData.fulfilled, (state, action) => {
        if (action.meta.requestId !== current(state).noSalesRequestId) {
          return;
        }
        const { data } = action.payload;
        state.status = "idle";

        if (data?.length > 0) {
          const vendorMap = {} as any;

          const formattedData = data.map((item: any) => {
            const retail = formatToCurrency(stringToNumber(item.retail));
            const value = formatToCurrency(stringToNumber(item.value));
            const salesRetail = formatToCurrency(
              stringToNumber(item.salesRetail)
            );
            const avgRetail = formatToCurrency(stringToNumber(item.avgRetail));
            const salesUnit = decimal(item.salesUnit);
            const margin = formatToPercentage(decimal(item.margin));
            const itemTypes = getItemTypes(item);

            if (item.vendorId && item.vendorName) {
              vendorMap[item.vendorId] = item.vendorName;
            }

            return {
              ...item,
              retail,
              value,
              salesRetail,
              avgRetail,
              salesUnit,
              margin,
              itemTypes,
              key: getComponentKey()
            };
          });

          state.data = formattedData;
          state.vendorMap = vendorMap;
        } else {
          state.data = [];
        }
      })
      .addCase(fetchNoSaleData.rejected, (state) => {
        state.status = "error";
        state.data = [];
      })
      .addCase(fetchNoSalesRecordsCount.pending, (state, action) => {
        state.countRequestId = action.meta.requestId;
      })
      .addCase(fetchNoSalesRecordsCount.fulfilled, (state, action) => {
        if (action.meta.requestId !== current(state).countRequestId) {
          return;
        }
        const { data } = action.payload;
        state.recordsCount = data?.count;
      })
      .addCase(fetchNoSalesFilterList.fulfilled, (state, { payload }) => {
        const { data } = payload;
        state.filters = data;
      })
      .addCase(fetchNoSalesListTags.fulfilled, (state, { payload }) => {
        state.tags = payload?.data?.tags?.map((item: string) => ({
          id: item,
          name: item
        }));
      });
  }
});

export const selectViewItemAlertsTableData = ({
  viewItemAlertsTable
}: {
  viewItemAlertsTable: NoSaleInitialState;
}) => viewItemAlertsTable.data;

export const selectViewItemAlertsTableApiStatus = ({
  viewItemAlertsTable
}: {
  viewItemAlertsTable: NoSaleInitialState;
}) => viewItemAlertsTable.status;

export const selectViewItemAlertsTableError = ({
  viewItemAlertsTable
}: {
  viewItemAlertsTable: NoSaleInitialState;
}) => viewItemAlertsTable.error;

export const selectNoSalesFilterData = (state: any) => state.noSale.filters;

export const selectNoSalesTagsData = (state: any) => state.noSale.tags;

export default viewNoSaleData.reducer;
